import { useEffect, useState } from 'react';
import { Agent } from '../../Providers/DashboardProvider';
import theme from '../../theme';
import { useAgents } from '../../hooks/useAgents';
import { Button, Menu, MenuItem, CircularProgress } from '@mui/material';
import { CaretDown } from '@phosphor-icons/react';
import { normalizePhoneAccountType } from '../../utils/phoneAccountType';

const menuStyles = {
  button: {
    textTransform: 'none',
    width: '50%',
    textAlign: 'center',
    borderRadius: '6px',
    // backgroundColor: isPhoneNumberPage ? '#FFFFFF' : '#F8F9FC',
    color: theme.palette.lightGray.text,
    fontSize: '14px',
    justifyContent: 'space-between',
    border: `1px solid ${theme.palette.lightGray.main}`,
    boxShadow: 'none',
    fontWeight: 400,
    '&:hover': {
      boxShadow: 'none',
    },
  },
  typography: {
    fontWeight: 400,
    fontSize: '14px',
  },
  menuItem: {
    fontSize: '14px',
    fontWeight: '400',
    lineHeight: '20px',
    color: theme.palette.text.secondary,
  },
} as const;

export type AgentsPhoneAccountFilter = 'trial' | 'prod';

interface AgentsDropDownProps {
  onAgentSelect: (agent: Agent) => void;
  isMultilingual?: boolean;
  isPhoneNumberPage?: boolean;
  isPending?: boolean;
  /** When set, only agents matching this phone account type appear (trial includes unset/legacy). */
  phoneAccountFilter?: AgentsPhoneAccountFilter;
}

const AgentsDropDown = ({
  onAgentSelect,
  isMultilingual = false,
  isPhoneNumberPage = false,
  isPending = false,
  phoneAccountFilter,
}: AgentsDropDownProps) => {
  const { agents } = useAgents();
  const [agentsList, setAgentsList] = useState<Agent[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuItemClick = (agent: Agent) => {
    onAgentSelect(agent);
    setAnchorEl(null);
  };

  useEffect(() => {
    if (agents) {
      const formattedAgents = agents?.agents
        ?.filter((agent: Agent) => {
          if (
            !agent.agentName ||
            agent.agentName.trim() === '' ||
            agent.agentChannel === 'whatsapp'
          ) {
            return false;
          }
          const normalized = normalizePhoneAccountType(agent.phoneAccountType);
          if (phoneAccountFilter === 'trial') {
            return normalized !== 'prod';
          }
          if (phoneAccountFilter === 'prod') {
            return normalized === 'prod';
          }
          return true;
        })
        ?.map((agent: Agent) => ({
          agentName: agent.agentName,
          _id: agent._id,
        }));
      setAgentsList(formattedAgents ?? []);
    }
  }, [agents, phoneAccountFilter]);

  return (
    <>
      <Button
        onClick={handleClick}
        variant="contained"
        sx={{
          ...menuStyles.button,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          width: isMultilingual ? '150px' : '100%',
          backgroundColor: isPhoneNumberPage ? '#FFFFFF' : '#F8F9FC',
        }}
      >
        {isPhoneNumberPage ? 'Assign To' : 'Use With'}
        {isPending ? (
          <CircularProgress size={16} />
        ) : (
          <CaretDown size={16} color={theme.palette.text.primary} />
        )}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        sx={{
          mt: '8px',
          '& .MuiPaper-root': {
            minWidth: '150px',
            width: 'fit-content',
            border: '1px solid',
            borderColor: 'lightGray.main',
            borderRadius: '6px',
            boxShadow: 'none',
          },
          '& .MuiList-root': {
            padding: '2px 0px',
          },
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        {agentsList.length === 0 ? (
          <MenuItem
            disabled
            sx={{ ...menuStyles.menuItem, padding: '6px 12px' }}
          >
            No eligible agents
          </MenuItem>
        ) : (
          agentsList.map(agent => (
            <MenuItem
              key={agent._id}
              onClick={() => handleMenuItemClick(agent)}
              sx={{
                ...menuStyles.menuItem,
                padding: '6px 12px',
              }}
            >
              {agent.agentName}
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
};

export default AgentsDropDown;
